import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchDemandsAndNeeds,
  fetchAddons,
  fetchAddonRationales,
  fetchActiveTemplate,
  indexRationales,
  saveDN,
  saveAddon,
  saveAddonRationale,
  saveTemplate,
  type ProductDN,
  type Addon,
} from '../../lib/compose';
import styles from './AdminTabs.module.css';

/**
 * Library admin (editLibrary capability). v1 = the in-app admin panel
 * Andy chose over a separate editor app. Edits the gaqc_* library tables
 * for one product at a time. Commercial Motor is the seeded sample.
 *
 * Scope note: this is a focused editor for the Stage-1 product, not a
 * full CMS. It edits the demand text, add-on descriptions, the two
 * rationale strings per add-on, and the SODAN template body. Adding
 * brand-new D&N / add-on rows from the UI is a fast-follow; for now new
 * rows come from the seed script and this panel edits them in place.
 */

const PRODUCTS = ['Commercial Motor', 'Personal Household Combined'];

export function LibraryTab() {
  const queryClient = useQueryClient();
  const [product, setProduct] = useState(PRODUCTS[0]);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError] = useState('');

  const libQ = useQuery({
    queryKey: ['compose.adminLibrary', product],
    queryFn: async () => {
      const [dn, addons, rationales, template] = await Promise.all([
        fetchDemandsAndNeeds(product),
        fetchAddons(product),
        fetchAddonRationales(),
        fetchActiveTemplate('SODAN', product),
      ]);
      return { dn, addons, rationaleIdx: indexRationales(rationales), template };
    },
  });

  // local editable copies, seeded from the query
  const [dnEdits, setDnEdits] = useState<Record<string, string>>({});
  const [addonDescEdits, setAddonDescEdits] = useState<Record<string, string>>({});
  const [rationaleEdits, setRationaleEdits] = useState<Record<string, { Take: string; Decline: string }>>({});
  const [templateBody, setTemplateBody] = useState('');
  const [seededFor, setSeededFor] = useState('');

  useEffect(() => {
    if (!libQ.data || seededFor === product) return;
    const dnMap: Record<string, string> = {};
    for (const d of libQ.data.dn) dnMap[d.gaqc_dnref] = d.gaqc_demandtext ?? '';
    const descMap: Record<string, string> = {};
    const ratMap: Record<string, { Take: string; Decline: string }> = {};
    for (const a of libQ.data.addons) {
      descMap[a.gaqc_addonref] = a.gaqc_description ?? '';
      ratMap[a.gaqc_addonref] = {
        Take: libQ.data.rationaleIdx[a.gaqc_addonref]?.Take ?? '',
        Decline: libQ.data.rationaleIdx[a.gaqc_addonref]?.Decline ?? '',
      };
    }
    setDnEdits(dnMap);
    setAddonDescEdits(descMap);
    setRationaleEdits(ratMap);
    setTemplateBody(libQ.data.template?.gaqc_body ?? '');
    setSeededFor(product);
  }, [libQ.data, product, seededFor]);

  const dnByRef = useMemo(() => {
    const m = new Map<string, ProductDN>();
    for (const d of libQ.data?.dn ?? []) m.set(d.gaqc_dnref, d);
    return m;
  }, [libQ.data]);
  const addonByRef = useMemo(() => {
    const m = new Map<string, Addon>();
    for (const a of libQ.data?.addons ?? []) m.set(a.gaqc_addonref, a);
    return m;
  }, [libQ.data]);

  const flash = (msg: string) => {
    setSavedMsg(msg);
    setTimeout(() => setSavedMsg(''), 2500);
  };

  const handleSaveDN = async (dnref: string) => {
    const base = dnByRef.get(dnref);
    if (!base) return;
    setError('');
    try {
      await saveDN({ ...base, gaqc_demandtext: dnEdits[dnref] ?? '' });
      flash(`Saved ${dnref}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  };

  const handleSaveAddon = async (addonref: string) => {
    const base = addonByRef.get(addonref);
    if (!base) return;
    setError('');
    try {
      await saveAddon({ ...base, gaqc_description: addonDescEdits[addonref] ?? '' });
      // rationale rows use the seed ref convention ADR-<addon-num>-<KIND>
      const num = addonref.replace('ADD-', '');
      const ed = rationaleEdits[addonref] ?? { Take: '', Decline: '' };
      await saveAddonRationale({
        gaqc_addondnref: `ADR-${num}-TAKE`,
        gaqc_name: `${base.gaqc_name} — Take`,
        gaqc_addonref: addonref,
        gaqc_decisionkind: 'Take',
        gaqc_rationale: ed.Take,
        gaqc_sortorder: 10,
        gaqc_status: 'Active',
      });
      await saveAddonRationale({
        gaqc_addondnref: `ADR-${num}-DECLINE`,
        gaqc_name: `${base.gaqc_name} — Decline`,
        gaqc_addonref: addonref,
        gaqc_decisionkind: 'Decline',
        gaqc_rationale: ed.Decline,
        gaqc_sortorder: 10,
        gaqc_status: 'Active',
      });
      flash(`Saved ${addonref}`);
      queryClient.invalidateQueries({ queryKey: ['compose.adminLibrary', product] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  };

  const handleSaveTemplate = async () => {
    const t = libQ.data?.template;
    if (!t) {
      setError('No template loaded to save.');
      return;
    }
    setError('');
    try {
      // Replace-not-edit for audit: bump the version on save.
      await saveTemplate({
        ...t,
        gaqc_body: templateBody,
        gaqc_version: (t.gaqc_version ?? 1) + 1,
        gaqc_status: 'Active',
      });
      flash('Saved template (version bumped)');
      queryClient.invalidateQueries({ queryKey: ['compose.adminLibrary', product] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  };

  return (
    <div className={styles.tab}>
      <header className={styles.header}>
        <h1 className={styles.title}>Library</h1>
        <p className={styles.lead}>
          Standard demands &amp; needs, add-ons and the SODAN template. Authored by compliance + admins; edited rarely.
        </p>
      </header>

      <div className={styles.productRow}>
        <label className={styles.productLabel} htmlFor="lib-product">Product</label>
        <select id="lib-product" className={styles.select} value={product} onChange={(e) => setProduct(e.target.value)}>
          {PRODUCTS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        {savedMsg && <span className={styles.savedFlash}>✓ {savedMsg}</span>}
      </div>

      {error && <div className={styles.errorBox}>{error}</div>}
      {libQ.isLoading && <p className={styles.muted}>Loading library…</p>}

      {libQ.isSuccess && (
        <>
          {/* Demands & needs */}
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Demands &amp; needs</h2>
            {libQ.data.dn.length === 0 && <p className={styles.muted}>No D&amp;N rows. Run the seed script to add the sample set.</p>}
            {libQ.data.dn.map((d) => (
              <div key={d.gaqc_dnref} className={styles.editRow}>
                <div className={styles.editHead}>
                  <span className={styles.editName}>{d.gaqc_name}</span>
                  <span className={styles.refTag}>{d.gaqc_dnref}</span>
                </div>
                <textarea
                  className={styles.editText}
                  rows={2}
                  value={dnEdits[d.gaqc_dnref] ?? ''}
                  onChange={(e) => setDnEdits((p) => ({ ...p, [d.gaqc_dnref]: e.target.value }))}
                />
                <div className={styles.editActions}>
                  <button type="button" className={styles.saveBtn} onClick={() => handleSaveDN(d.gaqc_dnref)}>Save</button>
                </div>
              </div>
            ))}
          </section>

          {/* Add-ons */}
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Add-ons</h2>
            {libQ.data.addons.length === 0 && <p className={styles.muted}>No add-on rows. Run the seed script to add the sample set.</p>}
            {libQ.data.addons.map((a) => (
              <div key={a.gaqc_addonref} className={styles.editRow}>
                <div className={styles.editHead}>
                  <span className={styles.editName}>{a.gaqc_name}</span>
                  <span className={styles.refTag}>{a.gaqc_addonref}</span>
                </div>
                <label className={styles.fieldLabel}>Description</label>
                <textarea
                  className={styles.editText}
                  rows={2}
                  value={addonDescEdits[a.gaqc_addonref] ?? ''}
                  onChange={(e) => setAddonDescEdits((p) => ({ ...p, [a.gaqc_addonref]: e.target.value }))}
                />
                <label className={styles.fieldLabel}>Rationale — if taken</label>
                <textarea
                  className={styles.editText}
                  rows={2}
                  value={rationaleEdits[a.gaqc_addonref]?.Take ?? ''}
                  onChange={(e) =>
                    setRationaleEdits((p) => ({
                      ...p,
                      [a.gaqc_addonref]: { Take: e.target.value, Decline: p[a.gaqc_addonref]?.Decline ?? '' },
                    }))
                  }
                />
                <label className={styles.fieldLabel}>Rationale — if declined</label>
                <textarea
                  className={styles.editText}
                  rows={2}
                  value={rationaleEdits[a.gaqc_addonref]?.Decline ?? ''}
                  onChange={(e) =>
                    setRationaleEdits((p) => ({
                      ...p,
                      [a.gaqc_addonref]: { Take: p[a.gaqc_addonref]?.Take ?? '', Decline: e.target.value },
                    }))
                  }
                />
                <div className={styles.editActions}>
                  <button type="button" className={styles.saveBtn} onClick={() => handleSaveAddon(a.gaqc_addonref)}>
                    Save add-on
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Template */}
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>SODAN template</h2>
            {!libQ.data.template ? (
              <p className={styles.muted}>
                No active SODAN template for {product}. Run the seed script to add the sample template.
              </p>
            ) : (
              <>
                <p className={styles.cardHint}>
                  Placeholders: <code>{'{{clientName}}'}</code> <code>{'{{productType}}'}</code>{' '}
                  <code>{'{{policyDescription}}'}</code> <code>{'{{insurer}}'}</code> <code>{'{{premium}}'}</code>{' '}
                  <code>{'{{brokerName}}'}</code> <code>{'{{today}}'}</code> <code>{'{{demandsAndNeeds}}'}</code>{' '}
                  <code>{'{{addonDecisions}}'}</code> <code>{'{{recommendation}}'}</code> <code>{'{{justification}}'}</code>.
                  Saving bumps the version (templates are replaced, not edited in place, for audit).
                </p>
                <textarea
                  className={styles.templateText}
                  rows={18}
                  value={templateBody}
                  onChange={(e) => setTemplateBody(e.target.value)}
                />
                <div className={styles.editActions}>
                  <span className={styles.versionTag}>v{libQ.data.template.gaqc_version ?? 1}</span>
                  <button type="button" className={styles.saveBtn} onClick={handleSaveTemplate}>
                    Save template
                  </button>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
